import React, { useMemo } from 'react';
import type { ReviewCommentViewItem } from './types';
import { formatCommentDate } from './utils';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_CARD,
  BG_SURFACE,
  FONT_HEADING,
} from '../../../components/legacy-ball/table-management/designTokens';

interface CommentsPanelProps {
  comments: ReviewCommentViewItem[];
  loading: boolean;
  emptyMessage?: string;
  /** When a specific submission is selected, show its header summary */
  selectedSubmissionId?: string | 'all';
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 4px',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 16px',
    color: TEXT_MUTED,
    fontSize: '0.95rem',
  },
  empty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 16px',
    color: TEXT_MUTED,
    fontSize: '0.95rem',
    fontStyle: 'italic',
  },
  // Submission header summary
  headerCard: {
    backgroundColor: BG_SURFACE,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '14px 20px',
    marginBottom: '16px',
    borderLeft: `4px solid ${PRIMARY}`,
  },
  headerTitle: {
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    fontSize: '1.05rem',
    color: TEXT_DARK,
    margin: '0 0 8px 0',
  },
  headerRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '16px',
    marginBottom: '4px',
  },
  headerField: {
    fontSize: '0.82rem',
    color: TEXT_DARK,
    lineHeight: 1.5,
  },
  headerLabel: {
    fontWeight: 600,
    color: TEXT_MUTED,
    fontSize: '0.75rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  headerValue: {
    fontWeight: 500,
    display: 'block',
    marginTop: '2px',
  },
  // Comment cards
  card: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '16px 20px',
    marginBottom: '12px',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '6px',
    gap: '12px',
  },
  commenterName: {
    fontFamily: FONT_HEADING,
    fontWeight: 600,
    fontSize: '0.95rem',
    color: TEXT_DARK,
    margin: 0,
  },
  date: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    whiteSpace: 'nowrap',
  },
  submissionTitle: {
    fontSize: '0.82rem',
    color: PRIMARY,
    fontWeight: 500,
    marginBottom: '10px',
  },
  commentText: {
    fontSize: '0.9rem',
    color: TEXT_DARK,
    lineHeight: 1.6,
    margin: 0,
    wordBreak: 'break-word',
  },
};

function SubmissionHeader({ comments }: { comments: ReviewCommentViewItem[] }) {
  // All comments in this view are from the same submission
  const first = comments[0];
  if (!first) return null;

  const category = first.sectionCategory;
  const commentCount = comments.length;

  if (category === 'TileSpacePurchasers') {
    return (
      <div style={styles.headerCard}>
        <div style={styles.headerTitle}>{first.submissionTitle}</div>
        <div style={styles.headerRow}>
          <div style={styles.headerField}>
            <span style={styles.headerLabel}>Author</span>
            <span style={styles.headerValue}>{first.submissionAuthor}</span>
          </div>
          <div style={styles.headerField}>
            <span style={styles.headerLabel}>In Memory Of</span>
            <span style={styles.headerValue}>{first.submissionInMemoryOf || '—'}</span>
          </div>
          <div style={styles.headerField}>
            <span style={styles.headerLabel}>Inscription</span>
            <span style={styles.headerValue}>{first.submissionInscription || '—'}</span>
          </div>
          <div style={styles.headerField}>
            <span style={styles.headerLabel}>Reviewers</span>
            <span style={styles.headerValue}>
              {first.submissionReviewers.length > 0
                ? first.submissionReviewers.join(', ')
                : 'None assigned'}
            </span>
          </div>
          <div style={styles.headerField}>
            <span style={styles.headerLabel}>Comments</span>
            <span style={styles.headerValue}>{commentCount}</span>
          </div>
        </div>
      </div>
    );
  }

  // Default: GeneralArticles and other categories
  return (
    <div style={styles.headerCard}>
      <div style={styles.headerTitle}>{first.submissionTitle}</div>
      <div style={styles.headerRow}>
        <div style={styles.headerField}>
          <span style={styles.headerLabel}>Author</span>
          <span style={styles.headerValue}>{first.submissionAuthor}</span>
        </div>
        {first.submissionBookletSection && (
          <div style={styles.headerField}>
            <span style={styles.headerLabel}>Booklet Section</span>
            <span style={styles.headerValue}>{first.submissionBookletSection}</span>
          </div>
        )}
        <div style={styles.headerField}>
          <span style={styles.headerLabel}>Reviewers</span>
          <span style={styles.headerValue}>
            {first.submissionReviewers.length > 0
              ? first.submissionReviewers.join(', ')
              : 'None assigned'}
          </span>
        </div>
        <div style={styles.headerField}>
          <span style={styles.headerLabel}>Comments</span>
          <span style={styles.headerValue}>{commentCount}</span>
        </div>
      </div>
    </div>
  );
}

export function CommentsPanel({ comments, loading, emptyMessage, selectedSubmissionId }: CommentsPanelProps) {
  // Determine if we should show the header (only when a single submission is selected)
  const showHeader = useMemo(() => {
    if (!selectedSubmissionId || selectedSubmissionId === 'all') return false;
    return comments.length > 0 && comments.every((c) => c.submissionId === comments[0].submissionId);
  }, [comments, selectedSubmissionId]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading} role="status" aria-label="Loading comments">
          Loading comments…
        </div>
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.empty}>
          {emptyMessage || 'No comments match the current selection.'}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {showHeader && <SubmissionHeader comments={comments} />}
      {comments.map((comment) => (
        <article key={comment.id} style={styles.card}>
          <div style={styles.cardHeader}>
            <span style={styles.commenterName}>{comment.commenterName}</span>
            <span style={styles.date}>{formatCommentDate(comment.createdAt)}</span>
          </div>
          {!showHeader && (
            <div style={styles.submissionTitle}>{comment.submissionTitle}</div>
          )}
          {comment.isRichText ? (
            <div
              style={styles.commentText}
              dangerouslySetInnerHTML={{ __html: comment.text }}
            />
          ) : (
            <p style={styles.commentText}>{comment.text}</p>
          )}
        </article>
      ))}
    </div>
  );
}
